import FuseAnimate from '@/@fuse/core/FuseAnimate';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { getAllRooms, getRooms } from '@/app/services/rooms/rooms';
import { getAllStaff } from '@/app/services/staff/staff';
import { schedulesList } from '@/app/services/staff/staffschedule';
import * as Actions from '@/app/store/actions';
import DayjsUtils from '@date-io/dayjs';
import { Avatar, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DatePicker as MuiDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import Calendar from './calendar';
import './calendar.css';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
	select: {
		'&:before': {
			borderBottom: 'none',
		},
		'&:after': {
			borderBottom: 'none',
		},
		'&:not(.Mui-disabled):hover::before': {
			borderBottom: 'none',
		},
		'& .MuiSelect-select:focus': {
			backgroundColor: 'inherit',
		},
		'& .MuiSvgIcon-root': {
			color: 'inherit',
		},
		color: 'inherit',
		'&:hover': {
			color: 'inherit',
		},
	},
});

const StaffScheduleListing = () => {
	const componentToPrint = useRef(null);
	const classes = useStyles();
	const dispatch = useDispatch();
	const [rooms, setRooms] = useState([]);
	const [isRoomLoading, setIsRoomLoading] = useState(false);
	const [searchRoomQuery, setRoomSearchQuery] = useState('');
	const [week, setWeek] = useState([]);
	const [schedules, setSchedules] = useState([]);
	const [isScheduleLoading, setScheduleLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [selectedRoom, setSelectedRoom] = useState(null);
	const [hasMore, setHasMore] = useState(false);
	const ref = useRef(null);
	const [allRooms, setAllRooms] = useState([]);
	const [allStaff, setAllStaff] = useState([]);
	const date = new Date();
	const navigate = useNavigate();

	useEffect(() => {
		let isMounted = true;
		const d = moment(date).startOf('week');
		const newWeek = [];
		for (let i = 0; i < 7; i++) {
			const date = d.add(1, 'days');
			newWeek.push(new Date(date));
		}
		setWeek(newWeek);
		getAllRooms().then(({ data }) => {
			if (!isMounted) return;
			setAllRooms(data);
		});
		getAllStaff().then(({ data }) => {
			if (!isMounted) return;
			setAllStaff(data);
		});
		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		let isMounted = true;
		setScheduleLoading(true);
		setSchedules([]);
		setPage(1);
		schedulesList(
			week[0] ? moment(week[0]).format('YYYY-MM-DD') : moment().startOf('week').add(1, 'days').format('YYYY-MM-DD'),
			week[6] ? moment(week[6]).format('YYYY-MM-DD') : moment().startOf('week').add(7, 'days').format('YYYY-MM-DD'),
			1,
			selectedRoom
		)
			.then(({ data }) => {
				if (!isMounted) return;
				setSchedules(data.data);
				if (data.current_page < data.last_page) {
					setHasMore(true);
					setPage(page + 1);
				} else {
					setHasMore(false);
				}
			})
			.catch(() => {
				if (!isMounted) return;
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch data, please refresh',
						variant: 'error',
					})
				);
			})
			.finally(() => {
				if (!isMounted) return;
				setScheduleLoading(false);
			});
		return () => {
			isMounted = false;
		};
	}, [selectedRoom, week]);

	useEffect(() => {
		let isMounted = true;
		setIsRoomLoading(true);
		setRooms([]);
		if (!searchRoomQuery) {
			getAllRooms()
				.then(({ data }) => {
					if (!isMounted) return;
					setRooms(data);
				})
				.catch(() => {
					if (!isMounted) return;
					dispatch(
						Actions.showMessage({
							message: 'Failed to fetch data, please refresh',
							variant: 'error',
						})
					);
				})
				.finally(() => {
					if (!isMounted) return;
					setIsRoomLoading(false);
				});
		} else {
			getRooms(searchRoomQuery, searchRoomQuery ? undefined : 1)
				.then(({ data }) => {
					if (!isMounted) return;
					setRooms(data.data);
				})
				.catch(() => {
					if (!isMounted) return;
					dispatch(
						Actions.showMessage({
							message: 'Failed to fetch data, please refresh',
							variant: 'error',
						})
					);
				})
				.finally(() => {
					if (!isMounted) return;
					setIsRoomLoading(false);
				});
		}
		return () => {
			isMounted = false;
		};
	}, [searchRoomQuery]);

	const handleSelectChange = (e) => {
		navigate(`/${e.target.value}`);
	};

	const handleWeekChange = (changeNumber) => {
		setHasMore(false);
		setPage(1);
		const newWeekArray = [];
		for (let i = 0; i < 7; i++) {
			const date = new Date(week[i]);
			newWeekArray[i] = new Date(date.setDate(date.getDate() + changeNumber));
			setWeek(newWeekArray);
		}
	};

	const handlePrint = useReactToPrint({
		content: () => componentToPrint.current,
	});

	const handleLoadMore = () => {
		setScheduleLoading(true);
		schedulesList(moment(week[0]).format('YYYY-MM-DD'), moment(week[6]).format('YYYY-MM-DD'), page, selectedRoom)
			.then(({ data }) => {
				setSchedules(schedules.concat(data.data));
				if (data.current_page >= data.last_page) {
					setHasMore(false);
				} else {
					setHasMore(true);
					setPage(page + 1);
				}
			})
			.catch(() => {
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch data, please refresh',
						variant: 'error',
					})
				);
			})
			.finally(() => {
				setScheduleLoading(false);
			});
	};

	const handleCalendarDateChange = (date) => {
		date = new Date(date.format('YYYY-MM-DD'));
		setHasMore(false);
		setPage(1);
		const newWeek = [];
		for (let i = 0; i < 7; i++) {
			newWeek[i] = new Date(moment(date).add(i, 'days'));
		}
		setWeek(newWeek);
	};

	return (
		<FuseAnimate animation="transition.slideLeftIn" duration={600}>
			<div className="staff-schedule-cont mx-auto">
				<div style={{ display: 'none' }}>
					<div className="p-32" ref={componentToPrint}>
						<div className="flex flex-row justify-between">
							<div>
								<img alt="logo" src="assets/images/logos/logo.png" />
							</div>
							<div style={{ textAlign: 'right' }}>
								<Typography variant="subtitle1">
									<span style={{ display: 'block', marginBottom: '-0.7em', writingMode: '' }}>
										642 Glan Eagles Street
									</span>{' '}
									<span>Hom Lake, Orlando, FL 38637</span>
								</Typography>
								<Typography variant="subtitle1">+1 365 986 1258</Typography>
								<Typography variant="subtitle1">perfectday@gmail.com</Typography>
							</div>
						</div>
						<div style={{ marginBottom: '20px' }}>
							<h3>
								<strong>Schedules</strong>
							</h3>
							<span>
								<strong>Date: </strong>
							</span>
							<span>{moment(date).format('dddd, DD MMMM YYYY')}</span>
						</div>
						<Calendar
							allRooms={allRooms}
							allStaff={allStaff}
							loading={isScheduleLoading}
							printTable="print-table"
							staffSchedules={schedules}
							week={week}
						/>
					</div>
				</div>
				<div className="flex justify-between">
					<span className="text-pd-xl self-end font-bold mr-28" style={{ fontSize: '20px' }}>
						Schedules
					</span>
					<div className="flex items-end grow" style={{ marginTop: 20, gap: 15 }}>
						<Autocomplete
							autoComplete={false}
							clearOnBlur={false}
							getOptionLabel={(option) => option.name}
							id="rooms"
							loading={isRoomLoading}
							loadingText="...Loading"
							onChange={(_e, v) => {
								setSelectedRoom(v?.id);
							}}
							options={rooms}
							renderInput={(params) => (
								<TextField
									{...params}
									autoComplete="off"
									label="Room"
									onChange={(e) => setRoomSearchQuery(e.target.value)}
									style={{ width: '250px' }}
								/>
							)}
							renderOption={(option) => (
								<div className="flex" style={{ gap: 10 }}>
									<Avatar src={option.photo} />
									<div>{option.name}</div>
								</div>
							)}
							sx={{ width: '100%' }}
						/>
						<CustomButton fontSize="15px" height="40" id="add-btn" padding="2px" variant="secondary" width="200px">
							<Select
								className={classes.select}
								defaultValue="staffschedule"
								id="isNew"
								name="isNew"
								onChange={handleSelectChange}
							>
								<MenuItem className="hidden" disabled value="staffschedule">
									+&nbsp; Staff Schedule
								</MenuItem>
								<MenuItem value="staff-schedule-workshiftschedule">Work Shift</MenuItem>
								<MenuItem value="staff-schedule-ptoschedule">PTO</MenuItem>
								<MenuItem value="staff-schedule-sickschedule">Sick</MenuItem>
							</Select>
						</CustomButton>
						<CustomButton height="40" onClick={handlePrint} variant="secondary" width="80px">
							<i aria-hidden="true" className="fa fa-print" /> Print
						</CustomButton>
					</div>
				</div>
				<div className="bg-white rounded mt-12 p-32" style={{ height: '66vh', overflow: 'scroll', overflowX: 'auto' }}>
					<div className="flex justify-between">
						<div>
							<div className="flex justify-between">
								<button onClick={() => handleWeekChange(-7)} type="button">
									<img alt="backward" src="assets/images/backward.png" />
								</button>
								<button
									className="font-extrabold"
									onClick={() => ref.current.click()}
									style={{ fontSize: '15px', margin: '0 15px', cursor: 'pointer', lineHeight: '2.2' }}
									type="button"
								>
									<p>
										{week[6] ? moment.months()[new Date(week[3]).getMonth()] : ''}, {new Date(week[3]).getFullYear()}
									</p>
								</button>
								<button onClick={() => handleWeekChange(7)} type="button">
									<img alt="forward" src="assets/images/forward.png" style={{ cursor: 'pointer' }} />
								</button>
							</div>
							<MuiPickersUtilsProvider utils={DayjsUtils}>
								<MuiDatePicker
									disabled={false}
									format="dd/MM/yyyy"
									inputRef={ref}
									maxDate={moment().add(1, 'years').format('MM/DD/YYYY')}
									minDate={moment().subtract(2, 'months').format('MM/DD/YYYY')}
									onChange={(date) => handleCalendarDateChange(date)}
									style={{ display: 'none' }}
								/>
							</MuiPickersUtilsProvider>
						</div>
						<div>
							<div className="schedule-option">
								<div className="flex justify-between items-center" style={{ padding: '0px 20px' }}>
									<img alt="" src="assets/images/week.png" style={{ marginRight: 5 }} />
									<span>Week</span>
								</div>
							</div>
						</div>
					</div>
					<div className="mt-28">
						<Calendar
							allRooms={allRooms}
							allStaff={allStaff}
							loading={isScheduleLoading}
							rooms={allRooms}
							staff={allStaff}
							staffSchedules={schedules}
							week={week}
						/>
						<div style={{ textAlign: 'center', marginTop: '10px' }}>
							{!isScheduleLoading && hasMore ? (
								<CustomButton
									height="40"
									onClick={() => {
										handleLoadMore();
									}}
									variant="secondary"
									width="200px"
								>
									Load More
								</CustomButton>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</FuseAnimate>
	);
};

export default StaffScheduleListing;
